import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  AppBar,
  Toolbar,
  Container,
  IconButton,
  Avatar,
  Menu,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import TextFile from '@icons/TextFile';
import Colors from '@constants/Colors';
import { useNavigate } from 'react-router-dom';

interface FileItem {
  id: string;
  name: string;
  type: string;
  size: string;
  modifiedDate: string;
}

// Generate random file names with .nii.gz extension
// const generateRandomFileName = (index: number): string => {
//   const prefixes = [
//     'brain', 'skull', 't1', 't2', 'flair',
//     'dwi', 'mri', 'scan', 'image', 'volume',
//     'subject', 'patient', 'dataset', 'sample', 'data',
//     'anatomical', 'functional', 'structural', 'mri_scan', 'neuro'
//   ];
  
//   const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
//   const number = Math.floor(Math.random() * 1000);
  
//   return `${prefix}_${number}.nii.gz`;
// };

// Generate 30 random files with .nii.gz extension
const generateRandomFiles = (): FileItem[] => {
  const files: FileItem[] = [];
  const fileTypes = ['NIFTI', 'Medical Image', 'Neuroimaging', 'MRI', 'Brain Scan'];
  const fileSizes = ['2.5 MB', '5.8 MB', '12.3 MB', '25.6 MB', '48.2 MB', '96.5 MB', '185 MB', '320 MB'];
  
  for (let i = 0; i < 30; i++) {
    files.push({
      id: `file-${i}`,
      name: `spleen_${i}.nii.gz`,
      type: fileTypes[Math.floor(Math.random() * fileTypes.length)],
      size: fileSizes[Math.floor(Math.random() * fileSizes.length)],
      modifiedDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000)
        .toLocaleDateString(),
    });
  }
  
  return files;
};

const NifiFiles: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [files] = useState<FileItem[]>(generateRandomFiles());

  const navigate = useNavigate();

  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) {
      return files;
    }
    
    const query = searchQuery.toLowerCase();
    return files.filter((file) =>
      file.name.toLowerCase().includes(query) ||
      file.type.toLowerCase().includes(query)
    );
  }, [files, searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <AppBar position="static" style={{ backgroundColor: '#f04a23', height: '75px', width: '100%' }}>
        <Container style={{ display: 'flex', alignItems: 'center', padding: '0px', height: '100%', margin: 'auto', width: 0, minWidth: '100%' }}>
          <Typography
            component="a"
            href="/projects"
            style={{ textDecoration: 'none' }}
          >
            <Toolbar style={{ backgroundColor: '#f04a23', height: '100%', width: '200px', textAlign: 'center' }}>
              <img style={{ width: '-webkit-fill-available', height: '55px' }} src="/genie-logo.png" alt='Logo' />
            </Toolbar>
          </Typography>
          <Typography variant="h6" noWrap component="div" style={{ fontWeight: 900, color: 'white', margin: 'auto' }}>
            NIFTI Files
          </Typography>

          <Box style={{ padding: '12px' }}>
            <IconButton sx={{ p: 0, backgroundColor: 'white' }}>
              <Avatar> A </Avatar>
            </IconButton>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={false}
            />
          </Box>
        </Container>
      </AppBar>

      <Box sx={{ display: 'flex', justifyContent: 'center', padding: { xs: '10px', sm: '14px 25px' }, marginTop: '10px', width: '100%' }}>
        <Paper
          elevation={3}
          sx={{
            padding: '24px',
            width: '100%',
            maxWidth: '1400px',
            margin: '0 auto',
            backgroundColor: Colors.neutralwhite_50,
          }}
        >
      <Box sx={{ marginBottom: '24px' }}>
        <TextField
          fullWidth
          placeholder="Search .nii.gz files by name or type..."
          value={searchQuery}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: Colors.neutral_gray_400 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: Colors.white,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: Colors.primary,
              },
              '&.Mui-focused fieldset': {
                borderColor: Colors.primary,
              },
            },
          }}
        />
        
        <Typography
          variant="body2"
          sx={{
            marginTop: '8px',
            color: Colors.neutral_gray_500,
          }}
        >
          Showing {filteredFiles.length} of {files.length} files
        </Typography>
      </Box>

      {filteredFiles.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            padding: '48px',
            color: Colors.neutral_gray_500,
          }}
        >
          <Typography variant="h6">No files found</Typography>
          <Typography variant="body2" sx={{ marginTop: '8px' }}>
            Try adjusting your search query
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredFiles.map((file) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={file.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: Colors.Elevation_4,
                  },
                  backgroundColor: Colors.white,
                  border: `1px solid ${Colors.neutral_gray_100}`,
                }}
                onClick={() => navigate('/dummy/segmentation-labelling-page')}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '20px 16px',
                    flexGrow: 1,
                    '&:last-child': {
                      paddingBottom: '20px',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '12px',
                      padding: '12px',
                      backgroundColor: Colors.neutralwhite_10,
                      borderRadius: '8px',
                      width: '64px',
                      height: '64px',
                    }}
                  >
                    <TextFile color={Colors.primary} />
                  </Box>
                  
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: Colors.neutralblack,
                      textAlign: 'center',
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                    title={file.name}
                  >
                    {file.name}
                  </Typography>
                  
                  {/* <Typography
                    variant="caption"
                    sx={{
                      color: Colors.neutral_gray_500,
                      textAlign: 'center',
                      marginBottom: '4px',
                    }}
                  >
                    {file.type}
                  </Typography> */}
                  
                  <Typography
                    variant="caption"
                    sx={{
                      color: Colors.neutral_gray_400,
                      textAlign: 'center',
                      fontSize: '0.7rem',
                    }}
                  >
                    {file.size}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
        </Paper>
      </Box>
    </Box>
  );
};

export default NifiFiles;

